import { TextField } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
  ChipContainer,
  SelectionLabel,
  StyledAutocomplete,
  StyledPopper,
  StyledSearchIcon,
} from "./singleSelectChip.styles";

export interface SingleSelectChipProps<T> {
  /** The label displayed on the chip */
  label: string;

  /** Array of available options */
  options: T[];

  /** Currently selected item (controlled mode) */
  value?: T | null;

  /** Initial value (uncontrolled mode) */
  defaultValue?: T | null;

  /** Callback when selection changes */
  onChange?: (selected: T | null) => void;

  /** Function to extract the display label from an option */
  getOptionLabel: (option: T) => string;

  /** Function to extract a unique key from an option */
  getOptionKey: (option: T) => string | number;

  /** Placeholder text for the search input */
  placeholder?: string;

  /** Custom icon displayed when no selection */
  icon?: React.ReactElement;

  /** Whether the chip is disabled */
  disabled?: boolean;
}

export const SingleSelectChip = <T,>({
  label,
  options,
  value,
  defaultValue,
  onChange,
  getOptionLabel,
  getOptionKey,
  placeholder = "Search...",
  icon,
  disabled = false,
}: SingleSelectChipProps<T>) => {
  // Determine if component is controlled
  const isControlled = value !== undefined;

  // Ref for the chip element to anchor the autocomplete
  const chipRef = useRef<HTMLDivElement>(null);

  // State for autocomplete open/closed
  const [open, setOpen] = useState(false);

  // Internal state for uncontrolled mode
  const [internalValue, setInternalValue] = useState<T | null>(defaultValue || null);

  // Get the current value (controlled or uncontrolled)
  const currentValue = isControlled ? value : internalValue;

  // Update internal state when defaultValue changes (uncontrolled mode only)
  useEffect(() => {
    if (!isControlled && defaultValue !== undefined) {
      setInternalValue(defaultValue);
    }
  }, [defaultValue, isControlled]);

  const handleToggle = () => {
    if (!disabled) {
      setOpen((v) => !v);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!disabled && event.key === " ") {
      event.preventDefault();
      handleToggle();
    }
    // Enter key is handled by onClick through MUI Chip
  };

  const handleChange = (_e: React.SyntheticEvent, newValue: T | null) => {
    // Update internal state for uncontrolled mode
    if (!isControlled) {
      setInternalValue(newValue);
    }

    // Call onChange callback
    if (onChange) {
      onChange(newValue);
    }

    // Close autocomplete after selection
    setOpen(false);
  };

  const handleClose = (_e: React.SyntheticEvent, reason: string) => {
    // Close on escape or blur, but not on select-option (handled in handleChange)
    if (reason === "escape" || reason === "blur") {
      setOpen(false);
    }
  };

  // Format the chip label
  const getChipLabel = () => {
    if (!currentValue) {
      return label;
    }

    return (
      <SelectionLabel>
        <span className="label-text">{label}:</span>
        <span className="item-text">{getOptionLabel(currentValue)}</span>
      </SelectionLabel>
    );
  };

  const hasSelection = currentValue !== null && currentValue !== undefined;

  return (
    <>
      <ChipContainer
        ref={chipRef}
        icon={hasSelection ? undefined : icon}
        label={getChipLabel()}
        variant="outlined"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        hasSelection={hasSelection}
        isOpen={open}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-label={
          hasSelection
            ? `${label}: ${getOptionLabel(currentValue)} selected. Press Enter to change.`
            : `${label}: No item selected. Press Enter to select.`
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      />
      <StyledPopper
        open={open}
        anchorEl={chipRef.current}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[
          { name: "flip", enabled: false },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              tether: false,
            },
          },
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
          {
            name: "computeStyles",
            options: {
              adaptive: false,
            },
          },
        ]}
      >
        <StyledAutocomplete
          open={open}
          onClose={handleClose}
          disablePortal
          options={options}
          value={currentValue}
          onChange={handleChange}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(a, b) => getOptionKey(a) === getOptionKey(b)}
          renderOption={(props, option) => {
            const key = String(getOptionKey(option));
            return (
              <li {...props} key={key}>
                {getOptionLabel(option)}
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              autoFocus
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <StyledSearchIcon />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          slotProps={{
            paper: { sx: { mt: 0 } },
            listbox: {
              sx: { maxHeight: 320 },
            },
          }}
        />
      </StyledPopper>
    </>
  );
};
